import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { typedDiscountNeedsApproval } from '@mlm/shared';
import { Badge, Button, MoneyInput, Textarea } from '@mlm/ui';
import {
  cashierDiscountFromBranch,
  previewCheckout,
  resolvePosVoucher,
  type CashierDiscountConfig,
} from '../lib/api';
import { checkoutTotals } from '../lib/checkout-totals';
import { walletWord } from '../lib/coin-label';
import { prettyError } from '../lib/pos-errors';
import { quickAmountLabel, quickAmounts } from '../lib/quick-amounts';
import { resolveTypedCode } from '../lib/till-code';
import { useCoinLabel } from '../lib/use-coin-label';
import { usePosSale, type PosSale } from '../lib/use-pos-sale';
import { DiscountPresets } from '../components/pos/discount-presets';
import { ManagerDiscountSection } from '../components/pos/manager-discount-section';
import { PaymentGrid } from '../components/pos/payment-grid';
import { ProductImage } from '../components/pos/product-image';
import { SlotBalances } from '../components/pos/slot-balances';
import { TotalsBox, type TotalsRow } from '../components/pos/totals-box';

/**
 * The checkout: the last screen before the charge. It shows what the server
 * will charge (the preview), takes the payment method, any fees, a discount
 * (a preset, a code, the cashier's typed discount) and the tender, and charges.
 *
 * It reads the sale from the same store as the till, so arriving here is a
 * plain navigation, and "Back" loses nothing. With nothing to check out it
 * goes back to the till; once a sale completes it hands over to the receipt.
 */
export function CheckoutPage() {
  const sale = usePosSale();
  if (sale.receipt) return <Navigate to={`/receipt/${sale.receipt.order.id}`} replace />;
  if (!sale.branchId || !sale.member || sale.cart.length === 0) {
    return <Navigate to={sale.stockistMode ? '/stockist' : '/'} replace state={{ resume: true }} />;
  }
  return <CheckoutBody sale={sale} />;
}

function CheckoutBody({ sale }: { sale: PosSale }) {
  const navigate = useNavigate();
  const coinLabel = useCoinLabel();
  const { cart, member, slotId, slotCode, branch, branchId, stockistMode, tenderKind } = sale;
  const home = stockistMode ? '/stockist' : '/';

  const paymentCurrency = sale.nonCash ? (sale.walletCurrency ?? 'cash') : 'cash';
  const items = useMemo(
    () => cart.map((l) => ({ productId: l.product.id, variantId: l.variant?.id, qty: l.qty })),
    [cart],
  );

  // The preview only depends on what the server actually computes — the line
  // stack (membership / rank / stockist) and the coupon, which is priced per
  // line. The cashier discount and the fees are layered on client-side
  // (`checkoutTotals`), so typing in those fields doesn't trigger a round-trip
  // + spinner on every keystroke. The server re-validates everything at
  // /pos/checkout.
  const coupon = sale.couponRequest(paymentCurrency);
  const previewQ = useQuery({
    queryKey: [
      'pos-preview',
      branchId,
      member?.id,
      slotId,
      paymentCurrency,
      stockistMode,
      JSON.stringify(items),
      coupon.discountPresetId ?? null,
      coupon.couponCode ?? null,
      coupon.voucherId ?? null,
    ],
    queryFn: () =>
      previewCheckout({
        branchId: branchId!,
        buyerUserId: member!.id,
        buyerSlotId: slotId,
        items,
        paymentCurrency,
        stockistTransfer: stockistMode,
        ...coupon,
      }),
    enabled: !!branchId && !!member && items.length > 0,
    // A coupon the server refuses is an answer, not a blip: retrying it only
    // delays telling the cashier why.
    retry: false,
  });
  const preview = previewQ.data;

  // A preset, code or gift check the server will not price (expired, used up,
  // nothing in the cart it applies to) is taken off again, and the reason stays
  // on screen. The totals then come back on their own, because the preview is
  // keyed on it.
  const [couponError, setCouponError] = useState<string | null>(null);
  const previewErrorCode =
    (previewQ.error as { response?: { data?: { error?: string } } } | null)?.response?.data
      ?.error ?? null;
  const hasCoupon = !!sale.coupon;
  const { setCoupon, voucherLabel } = sale;
  useEffect(() => {
    if (!hasCoupon || !previewErrorCode) return;
    if (
      !previewErrorCode.startsWith('coupon_') &&
      !previewErrorCode.startsWith('voucher') &&
      previewErrorCode !== 'discount_presets_disabled'
    )
      return;
    setCouponError(prettyError(previewErrorCode, coinLabel, voucherLabel));
    setCoupon(null);
  }, [hasCoupon, previewErrorCode, coinLabel, voucherLabel, setCoupon]);

  // A typed or scanned code: the buyer's gift check if it is one, else a
  // discount code for the preview to price (`lib/till-code.ts`).
  async function applyTypedCode(code: string): Promise<boolean> {
    setCouponError(null);
    const result = await resolveTypedCode({
      code,
      vouchersEnabled: sale.vouchersEnabled && !!member,
      codesEnabled: sale.promoCodesEnabled,
      lookupVoucher: (c) => resolvePosVoucher(c, member!.id),
    });
    if ('error' in result) {
      setCouponError(prettyError(result.error, coinLabel, voucherLabel));
      return false;
    }
    setCoupon(result.coupon);
    return true;
  }
  const couponTotal = Number(preview?.couponTotal ?? 0);

  const totals = preview
    ? checkoutTotals({
        goodsTotal: preview.total,
        currency: paymentCurrency,
        discountPct: sale.managerDiscount.pct,
        discountAmount: sale.managerDiscount.amount,
        fees: sale.selectedFees,
      })
    : null;

  // Cashier-discount policy lives on the BRANCH (per-store override authority).
  const cashierCfg: CashierDiscountConfig = cashierDiscountFromBranch(branch);
  const discountOffered = paymentCurrency === 'cash' && cashierCfg.enabled;
  const mdPct = Number(sale.managerDiscount.pct) || 0;
  const mdAmount = Number(sale.managerDiscount.amount) || 0;
  // "Above cap" on a branch that lets the cashier type a discount; every typed
  // discount on a branch set to presets only.
  const aboveCap =
    discountOffered &&
    typedDiscountNeedsApproval({
      mode: cashierCfg.mode,
      pct: mdPct,
      amount: mdAmount,
      maxPct: cashierCfg.max_pct_without_approval,
      maxAmount: cashierCfg.max_amount_without_approval,
    });
  const [showDiscount, setShowDiscount] = useState(mdPct > 0 || mdAmount > 0);
  // Presets and the code box: a peso discount on a retail sale. The sale's
  // coupon is kept while a GC / coin tender is selected, just not applied.
  const presetsOffered = paymentCurrency === 'cash' && !stockistMode;
  const presetNeedsPin =
    presetsOffered &&
    sale.coupon?.kind === 'preset' &&
    sale.coupon.requiresManagerPin &&
    !sale.couponChip;

  const fmt = (v: string | number) =>
    paymentCurrency === 'cash'
      ? `₱${Number(v).toFixed(2)}`
      : `${Number(v).toFixed(2)} ${walletWord(paymentCurrency, coinLabel)}`;

  const due = totals ? Number(totals.dueFixed) : 0;
  const tendered = Number(sale.amountTendered);
  const change =
    tenderKind === 'cash' && sale.amountTendered && tendered >= due ? tendered - due : null;
  const short = tenderKind === 'cash' && !!sale.amountTendered && tendered < due;

  const showPhotos = (sale.products ?? []).some((p) => p.images.length > 0);
  // The preview returns one line per item sent, in order. Pair them with the
  // cart so each row has its photo and variation name; if the two ever
  // disagree in length, fall back to the till's own prices rather than
  // mislabel a row.
  const paired = preview && preview.lines.length === cart.length;

  const rows: TotalsRow[] = [];
  if (preview && totals) {
    rows.push({ label: 'Subtotal', value: fmt(preview.subtotal) });
    if (Number(preview.discountTotal) > 0) {
      rows.push({
        label: 'Member pricing',
        value: `−${fmt(preview.discountTotal)}`,
        tone: 'discount',
      });
    }
    if (couponTotal > 0) {
      rows.push({
        label: preview.couponLabel || preview.couponCode || 'Discount',
        value: `−${fmt(preview.couponTotal!)}`,
        tone: 'discount',
      });
    }
    if (totals.cashierDiscount.gt(0)) {
      rows.push({
        label: 'Cashier discount',
        value: `−${fmt(totals.cashierDiscount.toFixed(4))}`,
        tone: 'discount',
      });
    }
    for (const f of totals.feeLines) rows.push({ label: f.label, value: `+${fmt(f.amount)}` });
  }
  const discounted =
    !!preview &&
    !!totals &&
    (Number(preview.discountTotal) > 0 || couponTotal > 0 || totals.cashierDiscount.gt(0));

  const canCharge =
    !!totals &&
    !sale.checkoutPending &&
    !!sale.methodCode &&
    (tenderKind !== 'cash' || (!!sale.amountTendered && tendered >= due)) &&
    !sale.tenderUnpriceable &&
    // A labelled fee or no fee — never an unexplained charge.
    !sale.feeAmountInvalid &&
    (!stockistMode || !!sale.stockistBranch) &&
    !sale.courseBlocked &&
    !(aboveCap && !sale.managerDiscount.approvalChip) &&
    !presetNeedsPin;

  return (
    <div className="mx-auto max-w-6xl">
      <button
        type="button"
        onClick={() => navigate(home, { state: { resume: true } })}
        disabled={sale.checkoutPending}
        className="mb-3 flex h-11 items-center gap-2 text-base font-medium text-muted-foreground"
      >
        <ArrowLeft className="h-5 w-5" /> Back to the till
      </button>

      <div className="grid gap-8 rounded-[10px] bg-card p-[25px] shadow-card lg:grid-cols-[minmax(0,1fr)_420px]">
        {/* ── What is being sold, and to whom ── */}
        <section className="min-w-0 space-y-5">
          <h1 className="text-2xl font-semibold">
            {stockistMode ? 'Transfer Details' : 'Checkout Details'}
          </h1>

          <dl className="space-y-1.5 text-base">
            <InfoRow label={stockistMode ? 'Stockist' : 'Customer'}>
              <span className="font-medium">{member!.fullName ?? member!.email}</span>
            </InfoRow>
            <InfoRow label="Email">{member!.email}</InfoRow>
            <InfoRow label="Slot">
              {slotCode ? (
                <span className="space-y-1">
                  <Badge variant="default" className="text-xs">
                    {slotCode}
                  </Badge>
                  <SlotBalances
                    slot={sale.selectedSlot}
                    showGc={sale.offersGc}
                    showCoin={sale.offersCoin}
                  />
                </span>
              ) : (
                <span className="text-warning">No active slot</span>
              )}
            </InfoRow>
            <InfoRow label="Branch">{branch?.name ?? '—'}</InfoRow>
          </dl>

          {stockistMode && sale.stockistBranch && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <span className="font-medium">Stockist stock transfer.</span> These items move into{' '}
              <span className="font-medium">{sale.stockistBranch.name}</span>&apos;s stock, not a
              retail sale.
            </div>
          )}

          <ul className="divide-y border-y">
            {cart.map((l, i) => {
              const pl = paired ? preview!.lines[i]! : null;
              const pct = pl ? Number(pl.appliedDiscountPct) : 0;
              return (
                <li key={`${l.product.id}:${l.variant?.id ?? ''}`} className="flex gap-3 py-3">
                  {showPhotos && (
                    <ProductImage
                      product={l.product}
                      className="h-[86px] w-[120px] shrink-0 rounded-[5px]"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold capitalize leading-tight">
                      {l.product.name}
                    </div>
                    {l.variant && (
                      <div className="text-sm text-muted-foreground">{l.variant.name}</div>
                    )}
                    <div className="mt-1 text-sm text-muted-foreground">
                      {l.qty} × {pl ? fmt(pl.unitPrice) : '…'}
                      {pct > 0 && <span className="ml-2 text-success">−{pct.toFixed(2)}%</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-base font-bold tabular-nums">
                    {pl ? fmt(Number(pl.unitPrice) * l.qty) : '…'}
                  </div>
                </li>
              );
            })}
          </ul>

          {previewQ.isError ? (
            <p className="text-base text-destructive">
              Could not work out the totals. Go back to the till and try again.
            </p>
          ) : !totals ? (
            <p className="text-base text-muted-foreground">Computing totals…</p>
          ) : (
            <TotalsBox
              rows={rows}
              total={fmt(totals.dueFixed)}
              struck={discounted ? fmt(preview!.subtotal) : undefined}
            />
          )}

          {presetsOffered && (
            <DiscountPresets
              presets={sale.discountPresets}
              codesEnabled={sale.promoCodesEnabled}
              vouchers={sale.buyerVouchers}
              vouchersEnabled={sale.vouchersEnabled}
              voucherLabel={voucherLabel}
              coupon={sale.coupon}
              couponChip={sale.couponChip}
              cartProductIds={items.map((i) => i.productId)}
              error={couponError}
              onCoupon={(next) => {
                setCouponError(null);
                sale.setCoupon(next);
              }}
              onApplyCode={applyTypedCode}
              onChip={sale.setCouponChip}
            />
          )}

          {discountOffered &&
            (showDiscount ? (
              <ManagerDiscountSection
                cfg={cashierCfg}
                pct={sale.managerDiscount.pct}
                amount={sale.managerDiscount.amount}
                reason={sale.managerDiscount.reason}
                approvalChip={sale.managerDiscount.approvalChip}
                setPct={(pct) => sale.patchManagerDiscount({ pct })}
                setAmount={(amount) => sale.patchManagerDiscount({ amount })}
                setReason={(reason) => sale.patchManagerDiscount({ reason })}
                setApprovalChip={(approvalChip) => sale.patchManagerDiscount({ approvalChip })}
                aboveCap={aboveCap}
              />
            ) : (
              <Button variant="outline" className="h-12" onClick={() => setShowDiscount(true)}>
                {cashierCfg.mode === 'presets_only' ? 'Manager discount' : 'Apply discount'}
              </Button>
            ))}
        </section>

        {/* ── How it is paid ── */}
        <section className="space-y-5">
          <h2 className="text-2xl font-semibold">Mode of Payment</h2>
          <PaymentGrid
            methods={sale.methods}
            selectedCode={sale.methodCode}
            onSelect={sale.setMethodCode}
            isDisabled={sale.methodDisabled}
          />
          {sale.methods.length === 0 && !sale.methodsLoading && (
            <p className="text-sm text-destructive">
              No payment methods configured. Set them up in Admin → POS payment methods.
            </p>
          )}

          {/* Ad-hoc charges. Above the tender on purpose — they change the
              amount due, so the cashier must see them before keying in what
              the customer handed over. No configured types ⇒ no control at
              all: an empty picker would be a dead affordance on every sale. */}
          {sale.customFeeAllowed && sale.feeTypes.length > 0 && (
            <div className="space-y-2">
              <div className="text-lg font-semibold">
                Fees <span className="text-sm font-normal text-muted-foreground">(optional)</span>
              </div>
              {sale.fees.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <select
                    className="h-12 min-w-0 flex-1 rounded-md border bg-background px-3 text-base"
                    value={f.typeCode}
                    onChange={(e) => sale.updateFee(i, { typeCode: e.target.value })}
                  >
                    {/* Only types not already used on another row, plus this
                        row's own — otherwise the same fee could be added twice
                        and the server would reject the sale at the till. */}
                    {sale.availableFeeTypes(f.typeCode).map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  {/* A computed fee's amount comes from the registry, so the
                      cashier reads it rather than types it — the server prices
                      it again and refuses an amount sent from here. */}
                  {sale.feeCalcFor(f.typeCode).kind === 'manual' ? (
                    <MoneyInput
                      value={f.amount}
                      onChange={(e) => sale.updateFee(i, { amount: e.target.value })}
                      placeholder="0.00"
                      aria-label={`${sale.feeLabelOf(f.typeCode)} amount`}
                      className="h-12 w-28 text-base"
                    />
                  ) : (
                    <div className="flex h-12 w-28 items-center justify-end rounded-md border bg-muted px-3 text-base tabular-nums">
                      {sale.feeRateLabel(f.typeCode)}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => sale.removeFee(i)}
                    aria-label={`Remove ${sale.feeLabelOf(f.typeCode)}`}
                    className="flex h-12 w-12 shrink-0 items-center justify-center text-muted-foreground"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ))}
              {sale.fees.length < sale.feeTypes.length && (
                <Button variant="outline" className="h-12" onClick={sale.addFeeRow}>
                  <Plus className="h-5 w-5" /> Add fee
                </Button>
              )}
              {sale.feeAmountInvalid && (
                <p className="text-sm text-destructive">
                  A fee can&apos;t be negative. Use the cashier discount to reduce a sale.
                </p>
              )}
            </div>
          )}

          {tenderKind === 'cash' && (
            <div className="space-y-2">
              <label className="text-lg font-semibold" htmlFor="tendered">
                Amount received
              </label>
              <div className="flex h-14 items-center rounded-lg border bg-background px-4 focus-within:ring-2 focus-within:ring-ring">
                <span className="text-2xl font-semibold text-muted-foreground">₱</span>
                <MoneyInput
                  id="tendered"
                  // Sized here (large figures), not by the till-wide field rule.
                  data-slot="tender-input"
                  value={sale.amountTendered}
                  onChange={(e) => sale.setAmountTendered(e.target.value)}
                  placeholder={totals ? Number(totals.dueFixed).toFixed(2) : '0.00'}
                  className="h-full border-0 bg-transparent px-2 text-2xl font-semibold focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              {totals && (
                <div className="grid grid-cols-3 gap-2">
                  {quickAmounts(due).map((a, i) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => sale.setAmountTendered(a.toFixed(2))}
                      className="h-12 rounded-lg bg-muted text-base font-semibold tabular-nums"
                    >
                      {i === 0 ? 'Exact' : `₱${quickAmountLabel(a)}`}
                    </button>
                  ))}
                </div>
              )}
              {change != null && (
                <div className="flex items-baseline justify-between pt-1">
                  <span className="text-lg font-semibold">Change</span>
                  <span className="text-2xl font-semibold tabular-nums">₱{change.toFixed(2)}</span>
                </div>
              )}
              {short && (
                <p className="text-sm text-destructive">
                  {fmt(due - tendered)} short of the total.
                </p>
              )}
            </div>
          )}
          {sale.nonCash && (
            <p className="text-base text-muted-foreground">
              {totals
                ? `Debits ${fmt(totals.dueFixed)} from the member's ${walletWord(sale.walletCurrency ?? 'coin', coinLabel)} wallet.`
                : `Debits the member's ${walletWord(sale.walletCurrency ?? 'coin', coinLabel)} wallet.`}
            </p>
          )}
          {tenderKind === 'exact' && totals && (
            <p className="text-base text-muted-foreground">
              Charge exactly {fmt(totals.dueFixed)} on the terminal. No change is given.
            </p>
          )}

          <div className="space-y-2">
            <label className="text-lg font-semibold" htmlFor="remarks">
              Remarks <span className="text-sm font-normal text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              id="remarks"
              rows={2}
              value={sale.remarks}
              onChange={(e) => sale.setRemarks(e.target.value)}
              placeholder="Note for this sale (e.g. reserved for pickup, special request)"
              maxLength={500}
              className="text-base"
            />
          </div>

          {sale.courseNotice && (
            <p className={`text-sm ${sale.courseBlocked ? 'text-destructive' : 'text-warning'}`}>
              {sale.courseNotice}
            </p>
          )}
          {stockistMode && !sale.stockistBranch && !sale.stockistBranchLoading && (
            <p className="text-sm text-destructive">
              This member does not own a stockist branch. Pick a stockist to transfer stock.
            </p>
          )}
          {sale.checkoutError && (
            <p
              className="rounded-md bg-destructive/10 px-3 py-2 text-base text-destructive"
              role="alert"
            >
              {prettyError(sale.checkoutError, coinLabel, voucherLabel)}
            </p>
          )}

          {aboveCap && !sale.managerDiscount.approvalChip && (
            <p className="text-sm text-warning">
              {cashierCfg.mode === 'presets_only'
                ? 'A typed discount needs a manager PIN before charging.'
                : 'This discount is above the cashier limit. A manager PIN is needed before charging.'}
            </p>
          )}

          <div className="grid grid-cols-[auto_1fr] gap-3">
            <button
              type="button"
              onClick={() => navigate(home, { state: { resume: true } })}
              disabled={sale.checkoutPending}
              className="h-[50px] rounded-[10px] border-2 border-foreground px-6 text-base font-semibold disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canCharge}
              onClick={() => totals && sale.submitCheckout(totals.dueFixed)}
              className="h-[50px] rounded-[10px] bg-primary px-4 text-base font-semibold text-primary-foreground disabled:opacity-40"
            >
              {sale.checkoutPending ? 'Charging…' : `Charge ${totals ? fmt(totals.dueFixed) : ''}`}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

/** A label / value row of the customer block (label column 130 px). */
function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[130px_minmax(0,1fr)] gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}
